import { SubtleIconButton } from "components/button";
import { useModalContext } from "components/modal";
import { differenceInDays, format } from "date-fns";
import * as React from "react";
import { RiPencilLine } from "react-icons/ri";
import { FiExternalLink } from "react-icons/fi";
import { TWarrantyTargetItem } from "types/warranty";
import CountdownRing from "./countdownRing";
import EditWarrantyModal from "./editWarrantyLineModal";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import TooltippedElement from "components/TooltippedElement";

type Props = {
  item: TWarrantyTargetItem;
  parentId: string;
  parentType: string;
};

export default function WarrantyLine({ item, parentId, parentType }: Props) {
  const { t } = useTranslation();
  const tBase = "containers.warrantyTab.warrantyLine";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const modal = useModalContext();

  // console.log("item", item);

  // console.log("parentType", parentType, parentId);

  const diffDay = item.start_date
    ? Math.max(
        0,
        differenceInDays(new Date(item.expiry_date!), new Date(item.start_date))
      )
    : null;

  const currDay =
    item.expiry_date &&
    differenceInDays(new Date(item.start_date!), new Date()) <= 0
      ? Math.max(0, differenceInDays(new Date(item.expiry_date), new Date()))
      : null;

  const color =
    currDay === 0
      ? "slate"
      : !currDay
      ? "blue"
      : currDay <= 30
      ? "red"
      : currDay <= 180
      ? "amber"
      : "green";

  let fromType: string, fromId: string, linkString: string;

  if (item.fk_request_id) {
    fromType = tr("Request");
    fromId = item.fk_request_id;
    linkString = "requests";
  } else if (item.fk_project_id) {
    fromType = tr("Project");
    fromId = item.fk_project_id;
    linkString = "projects";
  } else {
    fromType = tr("Property");
    fromId = item.fk_address_id;
    linkString = "property";
  }

  const isCreatedElsewhere = parentType === "request" 
    ? item.fk_request_id !== parentId
    : parentType === "project" 
    ? item.fk_project_id !== parentId
    : parentType === "property" && item.fk_request_id
    ? true
    : parentType === "property" && item.fk_project_id
    ? true
    : item.fk_address_id !== parentId;

  return (
    <div className={`flex items-center space-x-4 rounded-md ${item.archived ? "bg-red-100" : "bg-white"}`}>
      <CountdownRing {...{ color, item, diffDay, currDay }} />
      <div className="flex flex-col flex-1">
        <span className="flex items-center space-x-1 text-xs">
          <span className={`text-${color}-600 `}>
            {currDay === 0 ? (
              <span className="text-red-600">{tr("Expired")}</span>
            ) : !currDay ? (
              tr("Not yet started")
            ) : (
              `${currDay} ${tr("days left")}`
            )}
          </span>
          <span> | </span>
          {item.start_date && (
            <>
              <span className=" text-slate-500">
                {format(new Date(item.start_date), "MM/dd/yy")} -{" "}
                {format(new Date(item.expiry_date!), "MM/dd/yy")}
              </span>
              <span> | </span>
            </>
          )}

          <span className=" text-slate-500">
            {tr("Term")}:{" "}
            {`${item.duration} ${item.duration_type}${
              item.duration <= 1 ? "" : "s"
            }`}
          </span>
          <span> | </span>
          {isCreatedElsewhere ? (
            <Link to={`/${linkString}/${fromId}`} className="no-underline">
              {`${fromType} #${fromId}`}
            </Link>
          ) : (
            <span className=" text-slate-500">
              {`${fromType} #${fromId}`}
            </span>
          )}
        </span>
        <p>{item.label}</p>
      </div>
      <div>
        {parentType !== "property" && isCreatedElsewhere ? (
          <TooltippedElement
            message={tr("Edit on property")}
            placement="left"
            element={
              <Link
                to={`/property/${item.fk_address_id}`}
                className="no-underline text-gray-600"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FiExternalLink size={18} className="mr-[6px]" />
              </Link>
            }
          />
        ) : (
          <SubtleIconButton
            size="small"
            onClick={() =>
              modal.setModal({
                component: <EditWarrantyModal item={item} />,
                label: tr("Edit"),
              })
            }
          >
            <RiPencilLine />
          </SubtleIconButton>
        )}

      </div>
    </div>
  );
}
